import { sparkToast } from '@spark';
import { TaskType } from '@/shared/domain/taskTypes';
import { detectFileFormat } from '@/features/datasets/utils/filePreviewUtils';
import { formatApiErrorDetail } from '@/shared/utils/formatApiErrorDetail';

export function getFileName(rawFileName) {
    return rawFileName?.split(".")[0];
}

export function validateFile(file, options = {}) {
    const {
        selectedTaskType,
        currentCheckedDatasets = [],
        currentCheckedBenchmarks = [],
        isBenchmark = false,
        skipNameLengthCheck = false,
    } = options;

    // Check if task type is selected
    if (selectedTaskType === TaskType.none) {
        return {
            isValid: false,
            error: "Please select a task type before uploading files"
        };
    }

    // Check file name length (can be skipped for initial selection flow)
    if (!skipNameLengthCheck) {
        if (getFileName(file?.name)?.length > 40) {
            return {
                isValid: false,
                error: "File name must be less than 40 characters"
            };
        }
    }

    if (!detectFileFormat(file)) {
        return {
            isValid: false,
            error: "Invalid File Type"
        };
    }

    // Check for dataset/benchmark name mismatch
    const currentCheckedItems = isBenchmark ? currentCheckedBenchmarks : currentCheckedDatasets;
    const existingItemName = getFileName(currentCheckedItems[0]);

    if (existingItemName && existingItemName !== getFileName(file?.name)) {
        return {
            isValid: false,
            error: `${isBenchmark ? "Benchmark" : "Dataset"} name mismatch`
        };
    }

    return { isValid: true };
}

export function showUploadError(fileName, errorDetail = '') {
    const detail = formatApiErrorDetail(errorDetail);
    sparkToast.error('Upload Failed', {
        content: `${fileName} file upload failed. ${detail}`.trim(),
        detailed: true,
        duration: 6000,
    });
}

const VALIDATION_ERROR_TOASTS = {
    'Please select a task type before uploading files': {
        title: 'Task Type Required',
        content: 'Please select a task type before uploading files.',
    },
    'File name must be less than 40 characters': {
        title: 'File name must be less than 40 characters',
        content: 'Please rename the file and try again.',
    },
    'Invalid File Type': {
        title: 'Invalid File Type',
        content: 'You can only upload CSV, JSON, JSONL, or Parquet files!',
    },
    'Dataset name mismatch': {
        title: 'Dataset name mismatch',
        content: 'Only one dataset is allowed. Your upload must match the existing dataset name.',
    },
    'Benchmark name mismatch': {
        title: 'Benchmark name mismatch',
        content: 'Only one benchmark is allowed. Your upload must match the existing benchmark name.',
    },
};

export function showValidationError(error) {
    const toast = VALIDATION_ERROR_TOASTS[error] || { title: 'Upload Error', content: error };
    sparkToast.error(toast.title, {
        content: toast.content,
        detailed: true,
        duration: 6000,
    });
}

export function showNetworkError() {
    sparkToast.error('Upload Failed', {
        content: 'Network error occurred during upload.',
        detailed: true,
        duration: 6000,
    });
}